import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, FlaskConical, Shield, Award, Globe, CheckCircle, ChevronRight, TrendingUp } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { PeptideCard } from '../components/PeptideCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// USA Flag component
const USAFlag = ({ className = "w-5 h-4" }) => (
  <svg className={className} viewBox="0 0 60 40" xmlns="http://www.w3.org/2000/svg">
    <rect width="60" height="40" fill="#B22234"/>
    <rect y="3.08" width="60" height="3.08" fill="white"/>
    <rect y="9.23" width="60" height="3.08" fill="white"/>
    <rect y="15.38" width="60" height="3.08" fill="white"/>
    <rect y="21.54" width="60" height="3.08" fill="white"/>
    <rect y="27.69" width="60" height="3.08" fill="white"/>
    <rect y="33.85" width="60" height="3.08" fill="white"/>
    <rect width="24" height="21.54" fill="#3C3B6E"/>
    <g fill="white">
      {/* Stars - simplified 5x4 grid */}
      <circle cx="2.4" cy="2.15" r="0.8"/>
      <circle cx="7.2" cy="2.15" r="0.8"/>
      <circle cx="12" cy="2.15" r="0.8"/>
      <circle cx="16.8" cy="2.15" r="0.8"/>
      <circle cx="21.6" cy="2.15" r="0.8"/>
      <circle cx="4.8" cy="4.31" r="0.8"/>
      <circle cx="9.6" cy="4.31" r="0.8"/>
      <circle cx="14.4" cy="4.31" r="0.8"/>
      <circle cx="19.2" cy="4.31" r="0.8"/>
      <circle cx="2.4" cy="6.46" r="0.8"/>
      <circle cx="7.2" cy="6.46" r="0.8"/>
      <circle cx="12" cy="6.46" r="0.8"/>
      <circle cx="16.8" cy="6.46" r="0.8"/>
      <circle cx="21.6" cy="6.46" r="0.8"/>
      <circle cx="4.8" cy="8.62" r="0.8"/>
      <circle cx="9.6" cy="8.62" r="0.8"/>
      <circle cx="14.4" cy="8.62" r="0.8"/>
      <circle cx="19.2" cy="8.62" r="0.8"/>
    </g>
  </svg>
);

export default function HomePage() {
  const { t, language } = useLanguage();
  const [peptides, setPeptides] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [peptidesRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/peptides?sort=trending&lang=${language}`),
          axios.get(`${API}/peptides/categories?lang=${language}`),
        ]);
        setPeptides(peptidesRes.data);
        setCategories(categoriesRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [language]);

  const featuredPeptides = peptides.slice(0, 8);
  const topCategories = categories.slice(0, 6);

  const qualityFeatures = [
    {
      icon: Award,
      title: t('quality.purity'),
      text: t('quality.purityText'),
    },
    {
      icon: Shield,
      title: t('quality.coa'),
      text: t('quality.coaText'),
    },
    {
      icon: 'usa-flag',
      title: t('quality.usa'),
      text: t('quality.usaText'),
    },
    {
      icon: Globe,
      title: t('quality.export'),
      text: t('quality.exportText'),
    },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section - Black background with red accent */}
      <section className="relative bg-neutral-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img
            src="/images/hero-lab.png"
            alt="Scientists working in laboratory"
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center 20%' }}
          />
        </div>
        {/* Red gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-red-900/20 via-transparent to-transparent" />
        <div className="relative container-custom py-24 lg:py-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-600/20 border border-red-600/30 rounded-full mb-6">
              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
              <span className="text-xs font-medium text-red-300 uppercase tracking-wide">
                {t('quality.researchOnly')}
              </span>
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 animate-fadeIn"
              style={{ fontFamily: 'Chivo, sans-serif' }}
              data-testid="hero-title"
            >
              {t('hero.title')}
            </h1>
            <p className="text-lg sm:text-xl text-neutral-300 mb-8 max-w-2xl animate-fadeIn stagger-1">
              {t('hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fadeIn stagger-2">
              <Link
                to="/catalog"
                className="btn-primary inline-flex items-center justify-center gap-2"
                data-testid="hero-cta-catalog"
              >
                {t('hero.cta')}
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/coa"
                className="btn-secondary inline-flex items-center justify-center gap-2 bg-transparent border-white/30 text-white hover:bg-white/10"
                data-testid="hero-cta-coa"
              >
                {t('hero.learnMore')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quality Features */}
      <section className="py-16 bg-neutral-50 border-y border-neutral-200">
        <div className="container-custom">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {qualityFeatures.map((feature, index) => (
              <div
                key={index}
                className="flex flex-col items-center text-center p-4"
                data-testid={`quality-feature-${index}`}
              >
                <div className="p-3 bg-white border border-neutral-200 rounded-sm mb-4">
                  {feature.icon === 'usa-flag' ? (
                    <USAFlag className="w-6 h-5" />
                  ) : (
                    <feature.icon className="w-6 h-6 text-red-600" strokeWidth={1.5} />
                  )}
                </div>
                <h3 className="font-semibold text-neutral-900 text-sm mb-1 flex items-center gap-2">
                  {feature.icon === 'usa-flag' && <USAFlag className="w-4 h-3" />}
                  {feature.title}
                </h3>
                <p className="text-xs text-neutral-500">{feature.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="section bg-white" data-testid="categories-section">
        <div className="container-custom">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2
                className="text-2xl sm:text-3xl font-bold text-neutral-900"
                style={{ fontFamily: 'Chivo, sans-serif' }}
              >
                {t('categories.title')}
              </h2>
            </div>
            <Link
              to="/catalog"
              className="hidden sm:flex items-center gap-2 text-sm font-medium text-red-600 hover:text-red-700"
              data-testid="view-all-categories"
            >
              {t('categories.viewAll')}
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {topCategories.map((category, index) => {
              // Handle both translated (object) and simple (string) formats
              const categoryValue = typeof category === 'object' ? category.en : category;
              const categoryLabel = typeof category === 'object' ? category.translated : category;
              return (
                <Link
                  key={categoryValue}
                  to={`/catalog?category=${encodeURIComponent(categoryValue)}`}
                  className="group p-4 bg-neutral-50 border border-neutral-200 hover:border-red-600/50 hover:bg-white rounded-sm transition-all text-center"
                  data-testid={`category-${index}`}
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <div className="p-2 bg-white border border-neutral-200 rounded-sm w-10 h-10 flex items-center justify-center mx-auto mb-3 group-hover:border-red-200">
                    <FlaskConical className="w-5 h-5 text-neutral-600 group-hover:text-red-600" strokeWidth={1.5} />
                  </div>
                  <p className="text-sm font-medium text-neutral-700 group-hover:text-neutral-900 line-clamp-2">
                    {categoryLabel}
                  </p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured/Trending Peptides */}
      <section className="section bg-neutral-50" data-testid="featured-section">
        <div className="container-custom">
          <div className="flex items-center justify-between mb-10">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-6 h-6 text-red-600" />
              <h2
                className="text-2xl sm:text-3xl font-bold text-neutral-900"
                style={{ fontFamily: 'Chivo, sans-serif' }}
              >
                {t('quality.trendingPeptides')}
              </h2>
            </div>
            <Link
              to="/catalog"
              className="hidden sm:flex items-center gap-2 text-sm font-medium text-red-600 hover:text-red-700"
              data-testid="view-all-peptides"
            >
              {t('categories.viewAll')}
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid-products">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-white border border-neutral-200 p-6 rounded-sm animate-pulse">
                  <div className="h-4 bg-neutral-200 rounded w-1/4 mb-3" />
                  <div className="h-6 bg-neutral-200 rounded w-3/4 mb-2" />
                  <div className="h-4 bg-neutral-200 rounded w-1/2 mb-4" />
                  <div className="h-16 bg-neutral-200 rounded mb-4" />
                  <div className="h-8 bg-neutral-200 rounded" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid-products">
              {featuredPeptides.map((peptide, index) => (
                <div
                  key={peptide.cat_no}
                  className="animate-fadeIn"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <PeptideCard peptide={peptide} />
                </div>
              ))}
            </div>
          )}

          <div className="text-center mt-10 sm:hidden">
            <Link
              to="/catalog"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="view-all-mobile"
            >
              {t('categories.viewAll')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Scientific Excellence */}
      <section className="section bg-white" data-testid="excellence-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2
                className="text-2xl sm:text-3xl font-bold text-neutral-900 mb-6"
                style={{ fontFamily: 'Chivo, sans-serif' }}
              >
                {t('quality.title')}
              </h2>
              <p className="text-neutral-600 mb-8">
                {t('quality.subtitle')}
              </p>
              <div className="space-y-4">
                {[
                  t('quality.hplcAnalysis'),
                  t('quality.coaEveryBatch'),
                  t('quality.strictQuality'),
                  t('quality.lyophilized'),
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span className="text-neutral-700">{item}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8">
                <Link
                  to="/coa"
                  className="btn-dark inline-flex items-center gap-2"
                  data-testid="excellence-cta"
                >
                  {t('quality.viewCoaLibrary')}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1770195957512-b45ce419c00c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHw0fHxtZWRpY2FsJTIwdmlhbHMlMjBwaGFybWFjZXV0aWNhbCUyMG1hbnVmYWN0dXJpbmclMjBjbGVhbiUyMHJvb218ZW58MHx8fHwxNzcyNjY3MTc0fDA&ixlib=rb-4.1.0&q=85"
                alt="Laboratory Quality"
                className="rounded-sm shadow-lg"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 shadow-lg border border-neutral-200 rounded-sm">
                <p className="font-mono text-3xl font-bold text-red-600">99%+</p>
                <p className="text-sm text-neutral-600">{t('quality.purity')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
